import { createSlice } from "@reduxjs/toolkit";
import { API_BASE_URL, checkEndPoint } from "../config";
import { message } from "antd";
import { setError } from "./errorSlice";
import { createSafeThunk } from "../hooks/createSafeThunk";

export const fetchAllowedPages = createSafeThunk({
  typePrefix: "pageAccess/fetchAllowedPages",
  source: "pageAccess",

  validationFn: (candidatePages) => {
    const arrayPage = normalizePageNames(candidatePages);
    if (!arrayPage || arrayPage.length === 0) return "No page names provided.";
    const storedUser = localStorage.getItem("userInfo");
    if (!storedUser) return "User not authenticated.";

    const { token } = JSON.parse(storedUser);
    if (!token) return "Session expired. Please log in again.";
    return null;
  },

  apiCallFn: async (candidatePages) => {
    const arrayPage = normalizePageNames(candidatePages);
    const { token, roles } = JSON.parse(localStorage.getItem("userInfo"));
    const response = await fetch(`${API_BASE_URL}/api/PageRoleMapping/roles`, {
      method: "POST", //Note: it is pos just to use body: JSON.stringify({ roleNames: roles }),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ roleNames: roles }),
    });

    return {
      data: await response.json(),
      originalPages: arrayPage,
      isSuperAdmin: roles.includes("SuperAdmin"),
    };
  },
  onError: (error, dispatch) => {
    message.error(error.message);
    dispatch(setError(error.message));
  },
});

export const checkRegisterationOfPages = createSafeThunk({
  typePrefix: "pageAccess/checkRegisterationOfPages",
  source: "pageAccess",

  validationFn: (candidatePages) => {
    const arrayPage = normalizePageNames(candidatePages);
    if (!arrayPage || arrayPage.length === 0) return "No page names provided.";

    const storedUser = localStorage.getItem("userInfo");
    if (!storedUser) return "User not authenticated.";

    const { token } = JSON.parse(storedUser);
    if (!token) return "Session expired. Please log in again.";

    return null;
  },

  apiCallFn: async (candidatePages) => {
    const arrayPages = normalizePageNames(candidatePages);
    const arrayPage = arrayPages.map((page) => page.pagename);
    const { token } = JSON.parse(localStorage.getItem("userInfo"));
    const response = await fetch(
      `${API_BASE_URL}/api/PageRoleMapping/SingleRoleMultiPage`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pageName: arrayPage,
          roleName: "SuperAdmin",
        }),
      }
    );
    let payload = null;
    try {
      payload = await response.json(); // Parse even if not OK to read error payload
    } catch (e) {
      // Fail silently if response has no JSON
    }

    return {
      ok: response.ok,
      status: response.status,
      payload,
      originalPages: arrayPage,
    };
  },

  onError: (error, dispatch) => {
    message.error(error.message);
    dispatch(setError(error.message));
  },
});

const normalizePageNames = (this_Page_name) => {
  if (!this_Page_name) return [];

  if (
    Array.isArray(this_Page_name) &&
    this_Page_name.length > 0 &&
    typeof this_Page_name[0] === "object" &&
    "pagename" in this_Page_name[0]
  ) {
    return this_Page_name;
  }

  if (
    typeof this_Page_name === "object" &&
    !Array.isArray(this_Page_name) &&
    "pagename" in this_Page_name
  ) {
    return [this_Page_name];
  }

  if (Array.isArray(this_Page_name) && typeof this_Page_name[0] === "string") {
    return this_Page_name.map((p) => ({ pagename: p }));
  }

  return [];
};

const initialState = {
  hasAccess: false,
  hasallAccess: false,
  hasFewAccess: false,
  deniedPages: [],
  accessPages: [],
  accessLoading: false,
  error: null,
};

const pageAccessSlice = createSlice({
  name: "pageAccess",
  initialState,
  reducers: {}, // Defined reducers can be added if needed
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllowedPages.fulfilled, (state, action) => {
        state.accessLoading = false;

        const { data, originalPages, isSuperAdmin } = action.payload;
        if (isSuperAdmin && (!data || data.length === 0)) {
          state.hasAccess = true;
          state.accessPages = [];
          state.deniedPages = originalPages;
          state.hasallAccess = true;
          state.hasFewAccess = true;
          state.errorMessage = null;
          return;
        }
        if (!data || data.length === 0) {
          state.hasAccess = false;
          state.accessPages = [];
          state.deniedPages = originalPages;
          state.hasallAccess = false;
          state.hasFewAccess = false;
          state.errorMessage = "Invalid role to check page access";
          return;
        }
        const { deniedPages, allowedPages } = extractPageNames(
          data,
          originalPages
        );
        if (deniedPages.length === 0) {
          state.hasallAccess = true;
          state.hasAccess = true;
          state.hasFewAccess = true;
          state.deniedPages = [];
          state.accessPages = allowedPages;
          state.errorMessage = null;
        } else if (allowedPages.length === 0) {
          state.hasallAccess = false;
          state.hasFewAccess = false;
          state.hasAccess = false;
          state.deniedPages = deniedPages;
          state.accessPages = [];
          state.errorMessage = `No access to pages: ${deniedPages.join(", ")}`;
        } else {
          state.hasallAccess = false;
          state.hasFewAccess = true;
          state.hasAccess = true;
          state.deniedPages = deniedPages;
          state.accessPages = allowedPages;
          state.errorMessage = `No access to pages: ${deniedPages.join(
            ", "
          )} and granted access to pages ${allowedPages.join(", ")}`;
        }
      })

      .addCase(fetchAllowedPages.pending, (state) => {
        state.accessLoading = true; // Whenever loading starts
      })
      .addCase(fetchAllowedPages.rejected, (state, action) => {
        state.hasAccess = false; // Reset access on error
        state.accessLoading = false;
        state.error = action.payload; // Set error if any
      })
      .addCase(checkRegisterationOfPages.fulfilled, (state, action) => {
        const { ok, status, payload, originalPages } = action.payload;
        if (ok) {
          state.hasAccess = true;
          state.hasallAccess = true;
          state.hasFewAccess = true;
          state.deniedPages = [];
          state.accessPages = originalPages;
          state.errorMessage = null;
        } else {
          state.hasAccess = false;
          state.hasallAccess = false;
          state.hasFewAccess = false;
          state.deniedPages = originalPages;
          state.accessPages = [];
          state.errorMessage =
            (payload && payload.errorMessage) ||
            `Access denied (status ${status})`;
        }

        state.accessLoading = false;
      })
      .addCase(checkRegisterationOfPages.pending, (state) => {
        state.accessLoading = true; // Whenever loading starts
      })
      .addCase(checkRegisterationOfPages.rejected, (state, action) => {
        state.accessLoading = false;
        state.error = action.payload; // Set error if any
        state.hasAccess = false; // Reset access on error
      });
  },
});

export default pageAccessSlice.reducer;

export function extractPageNames(data, pagesName) {
  if (!Array.isArray(data)) {
    console.error("Input must be an array", data);
    return { deniedPages: pagesName ?? [], allowedPages: [] };
  }

  const pageList = data
    .map((item) => {
      if (typeof item === "object" && item !== null && "pageName" in item) {
        return item.pageName;
      }
      return null; // ✅ just skip invalid items
    })
    .filter((name) => name !== null);

  if (pageList.length > 0 && pagesName && pagesName.length > 0) {
    const deniedPages = pagesName.filter(
      (page) => !pageList.includes(page.pagename)
    );
    const allowedPages = pagesName.filter((page) =>
      pageList.includes(page.pagename)
    );

    return { deniedPages, allowedPages };
  }

  return {
    deniedPages: pagesName ?? [],
    allowedPages: [],
  };
}
